import React, { useEffect, useState } from "react";
import { useAuth } from "../../../../hooks";
import {
  convertUnitsFinal,
  convertUnitsWater,
  waterCodes,
  waterKPIsCodes,
} from "../../../../utils/dataAndFormulas/waterForms.js";
import { Column } from "@ant-design/plots";
import { useLanguage } from "../../../../contexts/LanguageContext";
import { formatWhole, formatMagnitude, formatMagnitudeAdaptive, formatPercentage } from "../../../../utils/numberFormat";
import "./WaterSummary.scss";

const WaterSummary = ({
  filters,
  site,
  valueBaseline,
  baselineLoaded = true,
  waterController,
  totales,
  workersTotals,
  selectorSlot,
  exportSlot,
}) => {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;

  const [data, setData] = useState({
    total: 0,
    alternativas: 0,
    produccion: 0,
    domestica: 0,
    intensidadIndus: { valor: 0 },
    intensidadDomes: { valor: 0 },
  });

  const [results, setResults] = useState({});

  const formatByRule = (item) => {
    switch (item.rule) {
      case "whole":
        return formatWhole(item.value);
      case "adaptive":
        return formatMagnitudeAdaptive(item.value);
      case "magnitude":
      default:
        return formatMagnitude(item.value);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        //console.log("📊 Totales recibidos en Resumen agua:", totales, filters);

        // Mientras se carga la línea base del sitio activo, `totales` puede
        // seguir siendo el del sitio anterior mientras `valueBaseline` ya se
        // reseteó a {} — evita calcular la intensidad como total/1 (el valor
        // absoluto disfrazado de normalizado) en esa ventana.
        if (!baselineLoaded) return;

        if (totales && Object.keys(totales).length > 0) {
          setResults(totales);

          const year = filters?.year;
          const yearData = totales?.[year];

          if (!yearData) {
            console.warn("⚠️ No hay datos para el año:", year);
            return;
          }

          const meses = Object.values(yearData);

          // 🔹 Sumar totales de todos los meses
          // 🔹 Asume que tenés definida la función convertUnitsModule o convertUnitsFinal
          // Ejemplo: convertUnitsModule(value, fromUnit, toUnit, field, module)

          const unidadObjetivo = "m3"; // Siempre queremos convertir a m³

          const total = meses.reduce((acc, mes) => {
            const valor = mes?.total_water_month?.total || 0;
            const unidad = mes?.total_water_month?.unit || "m3";
            const convertido = convertUnitsWater(
              valor,
              unidad,
              unidadObjetivo,
              "total_water_month",
              "water",
            );
            return acc + convertido;
          }, 0);

          // 🔹 Sumar TODAS las fuentes de agua de producción (primarias + recicladas)
          const alternativasKeys = [
            "rainwater",
            "reused_water",
            "recycled_water",
            "steam_condensed_water",
          ];

          const alternativas = meses.reduce((acc, mes) => {
            let totalMes = 0;

            alternativasKeys.forEach((key) => {
              const valor = mes?.[key]?.total || 0;
              const unidad = mes?.[key]?.unit || "m3";
              const convertido = convertUnitsWater(
                valor,
                unidad,
                unidadObjetivo,
                key,
                "water",
              );
              totalMes += convertido;
            });

            return acc + totalMes;
          }, 0);

          // 🔹 Sumar TODAS las fuentes de agua de producción (primarias + recicladas)
          const productionKeys = [
            "municipal_network_water_production",
            "groundwater_production",
            //"rainwater",
            "surface_water",
            "distilled_water",
            "reused_water",
            "recycled_water",
            "steam_condensed_water",
          ];

          const produccion = meses.reduce((acc, mes) => {
            let totalMes = 0;

            productionKeys.forEach((key) => {
              const valor = mes?.[key]?.total || 0;
              const unidad = mes?.[key]?.unit || "m3";
              const convertido = convertUnitsWater(
                valor,
                unidad,
                unidadObjetivo,
                key,
                "water",
              );
              totalMes += convertido;
            });

            return acc + totalMes;
          }, 0);

          // 🔹 Sumar solo las fuentes de agua doméstica
          const domesticKeys = [
            "water_domestic_municipal_network",
            "domestic_groundwater",
            "rainwater",
          ];

          const domestica = meses.reduce((acc, mes) => {
            let totalMes = 0;

            domesticKeys.forEach((key) => {
              const valor = mes?.[key]?.total || 0;
              const unidad = mes?.[key]?.unit || "m3";
              const convertido = convertUnitsWater(
                valor,
                unidad,
                unidadObjetivo,
                key,
                "water",
              );
              totalMes += convertido;
            });

            return acc + totalMes;
          }, 0);

          // Intensidad Industrial: total (en litros) / unidades de producción
          const totalL = total * 1000;
          const unidadesBaseProd = valueBaseline?.[filters.year] || 1;
          const intensidadValorProd =
            unidadesBaseProd > 0 ? totalL / unidadesBaseProd : 0;

          // Intensidad Doméstica: agua doméstica (en litros) / promedio de trabajadores
          const domesticaL = domestica * 1000;
          const promedioTrabajadores = workersTotals?.[filters.year] || 1;
          const intensidadValorDomest =
            promedioTrabajadores > 0 ? domesticaL / promedioTrabajadores : 0;

          setData({
            total,
            alternativas,
            produccion,
            domestica,
            intensidadProd: { valor: intensidadValorProd },
            intensidadDomes: { valor: intensidadValorDomest },
          });
        }
      } catch (error) {
        console.error("❌ Error al traer resumen de agua:", error);
      }
    };

    fetchData();
  }, [filters, site, valueBaseline, baselineLoaded, waterController, totales, workersTotals]);

  // --- Cálculo de Porcentajes ---
  const percentReutilizada =
    data.total > 0 ? (data.alternativas / data.total) * 100 : 0;
  const percentProd = data.total > 0 ? (data.produccion / data.total) * 100 : 0;
  const percentDomest =
    data.total > 0 ? (data.domestica / data.total) * 100 : 0;

  // Definir todos los posibles items en orden de prioridad
  const allPossibleItems = [
    {
      title: t("total_water"),
      value: data.total,
      unit: "M3",
      rule: "whole",
      secondary: "",
      progress: null,
      color: "blue",
      hasProgress: false,
    },
    {
      title: t("alternative_water"),
      value: data.alternativas,
      unit: "M3",
      rule: "whole",
      secondary: formatPercentage(percentReutilizada),
      progress: percentReutilizada,
      color: "blue",
      hasProgress: true,
    },
    {
      title: t("industrial_intensity"),
      value: data.intensidadProd?.valor || 0,
      unit: "M3 / Unidades",
      rule: "adaptive",
      secondary: "",
      progress: null,
      color: "blue",
      hasProgress: false,
    },
    {
      title: t("domestic_intensity"),
      value: data.intensidadDomes?.valor || 0,
      unit: "L / Trabajadores",
      rule: "whole",
      secondary: "",
      progress: null,
      color: "blue",
      hasProgress: false,
    },
    {
      title: t("industrial_water"),
      value: data.produccion,
      unit: "M3",
      rule: "magnitude",
      secondary: formatPercentage(percentProd),
      progress: percentProd,
      color: "blue",
      hasProgress: true,
    },
    {
      title: t("domestic_water"),
      value: data.domestica,
      unit: "M3",
      rule: "magnitude",
      secondary: formatPercentage(percentDomest),
      progress: percentDomest,
      color: "blue",
      hasProgress: true,
    },
  ];

  // Tomar solo los primeros 4 items según el orden de prioridad
  const resumenItems = allPossibleItems.slice(0, 4);

  return (
    <div className="resumen-grid">
      {resumenItems.map((item, index) => (
        <div
          key={index}
          className={`resumen-card-col${index === 0 ? " resumen-card-col--first" : ""}`}
        >
          <div className={`resumen-slot-row${index === resumenItems.length - 1 ? " resumen-slot-row--right" : ""}`}>
            {index === 0 && selectorSlot}
            {index === resumenItems.length - 1 && exportSlot}
          </div>
          <div
            className={`resumen-card water-card ${
              !item.hasProgress ? "no-progress" : ""
            }`}
          >
            <div className="card-meta">
              <div className="card-title-row">
                <span className="card-title">{item.title}</span>
              </div>
              <div>
                {item.subtitle && (
                  <span className="card-subtitle">{item.subtitle}</span>
                )}
              </div>
            </div>

            <div className="card-header">
              <span className="card-value">
                {formatByRule(item)}
                <span className="card-unit">{item.unit}</span>
              </span>
              {item.secondary && (
                <div className="percentage-text">{item.secondary}</div>
              )}
            </div>

            {item.hasProgress && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div
                    className="progress-fill"
                    style={{
                      width: `${Math.round(item.progress || 0)}%`,
                      backgroundColor: item.color || "#648EBC",
                    }}
                  />
                </div>
              </div>
            )}

            <div className="empty-space"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WaterSummary;
